<script>
import {
  trainerComputed,
  trainerMethods,
  authMethods,
  adminPanelComputed,
  adminPanelMethods,
  organizationComputed,
} from '@state/helpers';
import moment from 'moment';

export default {
  props: {
    charges: {
      type: Array,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {};
  },
  mounted() {},
  computed: {
    ...adminPanelComputed,
    ...trainerComputed,
    ...organizationComputed,
  },
  methods: {
    ...adminPanelMethods,
    ...trainerMethods,
    ...authMethods,
    getPaymentDescription(charge) {
      if (
        charge &&
        charge.invoice &&
        charge.invoice.lines &&
        charge.invoice.lines.data &&
        charge.invoice.lines.data.length > 0 &&
        charge.invoice.lines.data[0].description
      ) {
        return charge.invoice.lines.data[0].description;
      } else {
        return charge.id;
      }
    },
    getDate(date) {
      if (date) {
        return moment.unix(date).format('LL');
      }
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    getPaymentMethod(paymentMethodDetails) {
      if (paymentMethodDetails.type === 'card') {
        return (
          this.capitalizeFirstLetter(paymentMethodDetails.card.brand) +
          ' ' +
          paymentMethodDetails.card.last4
        );
      }
      if (paymentMethodDetails.type === 'ach_credit_transfer') {
        return this.capitalizeFirstLetter(
          paymentMethodDetails.ach_credit_transfer.bank_name
        );
      }
      if (paymentMethodDetails.type === 'ach_debit') {
        return (
          this.capitalizeFirstLetter(paymentMethodDetails.ach_debit.bank_name) +
          ' ' +
          paymentMethodDetails.ach_debit.last4
        );
      }
      if (paymentMethodDetails.type === 'au_becs_debit') {
        return (
          this.capitalizeFirstLetter(
            paymentMethodDetails.au_becs_debit.bsb_number
          ) +
          ' ' +
          paymentMethodDetails.au_becs_debit.last4
        );
      }
      if (paymentMethodDetails.type === 'bacs_debit') {
        return (
          this.capitalizeFirstLetter(
            paymentMethodDetails.bacs_debit.sort_code
          ) +
          ' ' +
          paymentMethodDetails.bacs_debit.last4
        );
      }
      if (paymentMethodDetails.type === 'card_present') {
        return (
          this.capitalizeFirstLetter(paymentMethodDetails.card_present.brand) +
          ' ' +
          paymentMethodDetails.card_present.last4
        );
      }
    },
    viewInvoice(url) {
      window.open(url, '_blank');
    },
  },
};
</script>

<template>
  <div class="payment-block-container">
    <div class="payment-header col">
      <div class="row">
        <div class="col">
          <label>amount</label>
        </div>
        <div class="col">
          <label>{{ organizationTitle }} PAY</label>
        </div>
        <div class="col">
          <label>date</label>
        </div>
        <div class="col">
          <label>payment method</label>
        </div>
      </div>
    </div>
    <div v-if="charges && charges.length > 0">
      <template v-for="charge in charges">
        <div
          :key="charge.id"
          class="summary-block col"
          @click="viewInvoice(charge.receipt_url)"
        >
          <div class="row">
            <div class="col">
              <span class="detail"
                >{{
                  charge.refunded
                    ? `($${charge.amount_refunded / 100})`
                    : `$${charge.amount / 100}`
                }}
              </span>
            </div>
            <div class="col">
              <span class="sub-name"
                >{{ charge.refunded ? 'Refund - ' : ''
                }}{{ getPaymentDescription(charge) }}</span
              >
            </div>
            <div class="col">
              <span class="detail">{{ getDate(charge.created) }}</span>
            </div>
            <div class="col">
              <span class="detail">{{
                getPaymentMethod(charge.payment_method_details)
              }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div v-else>
      <div class="no-transactions col">
        <div class="row">
          <div class="col">
            <span class="detail">No payments to display</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.payment-block-container {
  margin-top: 2rem;
  width: 100%;
}

.payment-header {
  align-items: center;
  background: rgba($color-medium-gray-wo-rgb, 0.33);
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba($color-black-wo-rgb, 0.08);
  display: flex;
  height: 5rem;
  margin-bottom: 2.2rem;
  mix-blend-mode: normal;
  .row {
    margin-left: 2rem;
    width: 85%;
    label {
      margin-bottom: unset;
    }
  }
}

.summary-block {
  align-items: center;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba($color-black-wo-rgb, 0.08);
  cursor: pointer;
  display: flex;
  height: 7rem;
  margin-bottom: 1rem;
  position: relative;
  transition: transform 300ms;
  transition-timing-function: ease-out;
  &:hover {
    box-shadow: 0 0.4rem 0.8rem rgba($color-black-wo-rgb, 0.1);
    transform: scale(1.01);
  }
  .row {
    margin-left: 2rem;
    width: 85%;
    .col {
      align-items: center;
      display: flex;
    }
    .detail {
      font-size: 18px;
      font-weight: normal;
      line-height: 21px;
      margin-bottom: unset;
    }
    .sub-name {
      -webkit-box-orient: vertical;
      display: block;

      /* Fallback for non-webkit */
      display: -webkit-box;
      font-size: 18px;
      font-style: normal;
      font-weight: normal;
      height: auto;
      letter-spacing: -0.3px;

      /* Fallback for non-webkit */
      -webkit-line-clamp: 2;
      line-height: 21px;
      margin-bottom: unset;
      max-width: 100%;
      overflow: hidden;
      text-align: left;
      text-overflow: ellipsis;
      word-break: break-all;
    }
  }
}

.no-transactions {
  align-items: center;
  background: rgba($color-medium-gray-wo-rgb, 0.33);
  border-radius: 12px;
  display: flex;
  height: 7rem;
  margin-bottom: 1rem;
  position: relative;
  .row {
    margin-left: 2rem;
    width: 85%;
    .col {
      align-items: center;
      display: flex;
    }
    .detail {
      font-size: 18px;
      font-weight: normal;
      line-height: 21px;
      margin-bottom: unset;
    }
  }
}
</style>
